/**
 * BeaconClient (F6) — the facade sim agents (B2) and the webapp (C2+) hold.
 * Wires identity + clock + transport + registry + directory. Genesis discipline:
 * publish() refuses until announce() has put declareKeys on the log (the chain
 * fold needs genesis first; relay A1 will enforce the same at ingress).
 *
 * Recovery-bundle contract: BeaconClient.create returns the bundle exactly ONCE,
 * at identity creation (live keys are non-extractable afterwards). rotateKeys()
 * rotates BOTH the signing and the encryption key (rotateKey.newKey +
 * .newEncryptionKey — chain.ts folds both) and returns a complete fresh bundle;
 * the previous recovery file is then rotated away and should be destroyed.
 */
import type { Clock } from "@beacon/clock";
import { utf8 } from "@beacon/crypto";
import type { FirehoseEvent, FirehoseTransport, PublishAck, StreamClass } from "@beacon/transport";
import { buildEnvelope, type EventInput, type SignedEnvelope } from "./envelope.js";
import { buildGeo, type GeoJson } from "./geo.js";
import { createIdentity, importIdentityBundle, type IdentityBundle, type IdentityKeys } from "./identity.js";
import { EpochKeys, type KeyRegistry } from "./epoch-keys.js";
import { buildKeyUploadBody } from "./http-key-registry.js";
import {
  openBroadcastJson, openNeedToKnowJson, sealBroadcastJson, sealBroadcastWithKey, sealNeedToKnowJson,
  type SealedBroadcastJson, type SealedNeedToKnowJson,
} from "./seal-helpers.js";
import { IdentityDirectory } from "./directory.js";
import {
  Subscription, verifyEvent,
  type ReceivedEvent, type SubscriptionContext, type VerifyContext,
} from "./subscription.js";

/** A relay reject that means "the relay holds no valid identity chain for us"
 *  (services/relay/directory.go). A non-identity event rejects `unknown-identity`
 *  (no chain head); an identity continuation like rotateKey rejects `genesis-invalid`
 *  (its candidate fold has no genesis). Both are healed the same way: re-emit our
 *  genesis. Duck-typed on `reason` — client-core is transport-agnostic and must not
 *  import the webapp's RelayError. */
function isMissingIdentityChain(err: unknown): boolean {
  if (typeof err !== "object" || err === null) return false;
  const reason = (err as { reason?: unknown }).reason;
  return reason === "unknown-identity" || reason === "genesis-invalid";
}

/** Fallback res-8 cell for a registry self-heal when no home cell is known (a
 *  restored pre-reset DID whose owner never set a home location). declareKeys geo
 *  is only a self-cert anchor — identity events route by keyed durable subject, not
 *  cell — so any valid res-8 cell re-establishes the chain. Same committed lexicon
 *  fixture the location-less contribution service declares at. Without this the heal
 *  no-ops and the pre-publish epoch-key PUT stays 403 unknown-identity (Talk never
 *  sends). announce()/publish()/a configured homeCell still take precedence. */
const HEAL_FALLBACK_CELL = "881faca331fffff";

export interface BeaconClientOptions {
  transport: FirehoseTransport;
  clock: Clock;
  registry: KeyRegistry;
  /** Restore path: identity already has its genesis on the log. */
  identity?: IdentityBundle;
  /**
   * Use these FIXED keys but emit a fresh genesis (announced=false). For
   * provisioning a known identity into a fresh log, e.g. the sim trust root.
   * Distinct from `identity`, which assumes genesis is already on the log.
   */
  genesisIdentity?: IdentityBundle;
  /**
   * The cell to re-emit genesis at when self-healing a wiped relay during an epoch-key
   * upload (registry `unknown-identity`). The restore path is `announced=true` and never
   * calls announce(), so without this the registry heal has no geo to declare at. The
   * webapp passes its persisted home cell; announce()/publish() also keep it fresh.
   */
  homeCell?: string;
}

export class BeaconClient {
  readonly directory = new IdentityDirectory();
  #keys: IdentityKeys;
  #epochKeys: EpochKeys;
  #announced: boolean;
  #lastRotationTid: string | null = null;
  #genesisGeo: GeoJson | undefined;

  private constructor(
    private readonly opts: BeaconClientOptions,
    keys: IdentityKeys,
    announced: boolean,
  ) {
    this.#keys = keys;
    this.#announced = announced;
    this.#genesisGeo = opts.homeCell ? buildGeo(opts.homeCell) : undefined;
    this.#epochKeys = new EpochKeys(keys.did, opts.registry);
    // Sign epoch-key uploads with our live keys (read at call time → rotation-safe).
    // No-op for registries without a signer seam (InMemoryKeyRegistry).
    this.opts.registry.bindUploader?.((keyRef, key) =>
      buildKeyUploadBody(
        { did: this.#keys.did, signingKey: this.#keys.signingKey, signingPrivate: this.#keys.signingPrivate },
        keyRef, key,
      ));
    // Self-heal a wiped relay surfaced by an epoch-key upload (registry
    // unknown-identity): re-emit genesis so the retried PUT resolves. The upload
    // precedes any publish, so publish()'s own heal can't cover this path.
    this.opts.registry.bindHealer?.(async () => {
      // Fall back to a neutral genesis cell when no home is known — else a restored
      // pre-reset DID with no homeCell can never re-declare and stays locked out.
      const geo = this.#genesisGeo ?? buildGeo(HEAL_FALLBACK_CELL);
      await this.#emitGenesis(geo);
    });
  }

  /** Fresh identity ⇒ bundle returned ONCE (the manual-recovery file). Imported ⇒ no bundle. */
  static async create(opts: BeaconClientOptions): Promise<{ client: BeaconClient; bundle?: IdentityBundle }> {
    if (opts.identity) {
      const keys = await importIdentityBundle(opts.identity);
      return { client: new BeaconClient(opts, keys, true) };
    }
    if (opts.genesisIdentity) {
      const keys = await importIdentityBundle(opts.genesisIdentity);
      return { client: new BeaconClient(opts, keys, false) }; // fixed keys, fresh genesis
    }
    const { keys, bundle } = await createIdentity();
    return { client: new BeaconClient(opts, keys, false), bundle };
  }

  get did(): string { return this.#keys.did; }
  geo(cell: string, o?: Parameters<typeof buildGeo>[1]): GeoJson { return buildGeo(cell, o); }

  /** Genesis declareKeys — must be this identity's first event (ADR-12 self-certification). */
  async announce(cell: string): Promise<PublishAck> {
    if (this.#announced) throw new Error("client: already announced");
    return this.#emitGenesis(buildGeo(cell));
  }

  /** Build, publish, and locally ingest a genesis declareKeys for the current keys
   *  at `geo`. Shared by announce() and the unknown-identity self-heal below. */
  async #emitGenesis(geo: GeoJson): Promise<PublishAck> {
    this.#genesisGeo = geo; // remember where we last declared, for registry-heal re-emits
    const env = await buildEnvelope(
      { $type: "app.aid.identity.declareKeys", payload: { encryptionKey: this.#keys.encryptionKey }, geo },
      this.#keys, this.opts.clock,
    );
    const ack = await this.opts.transport.publish(env.wire);
    this.#announced = true;
    this.directory.ingest(env.cbor); // know thyself
    return ack;
  }

  async publish(input: EventInput): Promise<PublishAck & { id: string; envelope: SignedEnvelope }> {
    if (!this.#announced) throw new Error("client: announce() before publish() — genesis must be first");
    const env = await buildEnvelope(input, this.#keys, this.opts.clock);
    const ack = await this.#publishHealing(env.wire, input.geo);
    return { ...ack, id: env.wire.id, envelope: env };
  }

  /** Publish a prebuilt envelope, self-healing a missing-chain reject: re-emit our
   *  genesis declareKeys (same keys, at `genesisGeo`) and retry the SAME envelope
   *  once. The trigger is a sim redeploy wiping the relay log while we kept our
   *  persisted bundle (announced=true), so we never re-announce. Scoped to
   *  unknown-identity / genesis-invalid, so a bad-sig / rate-limit / lexicon /
   *  chain-reject surfaces unretried (no loop); if the re-emitted genesis is itself
   *  rejected (e.g. keys were rotated away from the genesis key, so it can no longer
   *  self-certify) that error surfaces instead. */
  async #publishHealing(wire: FirehoseEvent, genesisGeo: GeoJson): Promise<PublishAck> {
    try {
      return await this.opts.transport.publish(wire);
    } catch (err) {
      if (!isMissingIdentityChain(err)) throw err;
      await this.#emitGenesis(genesisGeo);
      return await this.opts.transport.publish(wire);
    }
  }

  /**
   * Build a signed, lexicon-valid envelope WITHOUT publishing to the firehose.
   * Private registrations — `app.aid.helper.availability` → matcher (A7),
   * `app.aid.alert.subscription` → alert-dispatcher (A6) — are POSTed straight
   * to the owning service, which verifies the signature against the envelope's
   * own `signingKey` (never the public firehose). `env.wire.bytes` is the body.
   */
  async sign(input: EventInput): Promise<SignedEnvelope> {
    if (!this.#announced) throw new Error("client: announce() before sign() — genesis must be first");
    return buildEnvelope(input, this.#keys, this.opts.clock);
  }

  /** Convenience used everywhere in the sim: broadcast-sealed app.aid.message. */
  async publishSealedMessage(cell: string, text: string, opts?: { lang?: string; replyTo?: string }) {
    const sealed = await this.sealBroadcast(utf8(text));
    const payload: Record<string, unknown> = { text: sealed };
    if (opts?.lang) payload.lang = opts.lang;
    if (opts?.replyTo) payload.replyTo = opts.replyTo;
    return this.publish({ $type: "app.aid.message", payload, geo: buildGeo(cell) });
  }

  subscribe(
    filter: { cells?: string[]; types?: string[]; sinceSeq?: number; lookback?: number; class?: StreamClass },
    ctx?: Partial<Omit<SubscriptionContext, "directory" | "clock">>,
  ): Subscription {
    return new Subscription(this.opts.transport, filter, {
      directory: this.directory,
      clock: this.opts.clock,
      ...ctx,
    });
  }

  /** Current firehose head seq (last assigned; 0 when empty) — used to bound a
   *  backward backfill without replaying the whole log. */
  async head(): Promise<number> {
    return this.opts.transport.head();
  }

  /**
   * Finite, decoded backfill over an inclusive [fromSeq, toSeq] window: each
   * transport event is run through the SAME parse/verify path as the live
   * subscribe (verifyEvent), so callers get ReceivedEvents (skips dropped).
   * No dedupe ring — disjoint windows don't redeliver, and downstream stores
   * dedupe by id. Identity events in the window are ingested into the directory
   * as a side effect, exactly as on the live stream.
   */
  async *backfill(
    filter: { cells?: string[]; types?: string[] },
    range: { fromSeq: number; toSeq: number },
    ctx?: Partial<VerifyContext>,
  ): AsyncIterable<ReceivedEvent> {
    const vctx: VerifyContext = { directory: this.directory, ...ctx };
    for await (const evt of this.opts.transport.backfill(filter, range)) {
      const out = await verifyEvent(evt, vctx);
      if (out) yield out;
    }
  }

  async sealBroadcast(pt: Uint8Array): Promise<SealedBroadcastJson> {
    // clock.now() here and again inside the publish's buildEnvelope: both land
    // in the same keyRef epoch (EPOCH ≫ any in-between delay at rate 0/realtime).
    return sealBroadcastJson(this.#epochKeys, this.opts.clock.now(), pt);
  }
  /** Snapshot the current broadcast key epoch so a report's text + photos share one keyRef (E1). */
  async epochSnapshot(): Promise<{ keyRef: string; key: Uint8Array }> {
    return this.#epochKeys.current(this.opts.clock.now());
  }
  /** Seal under a fixed epoch snapshot — every field gets the identical keyRef (E1, photo spec §1). */
  async sealBroadcastWith(snap: { keyRef: string; key: Uint8Array }, pt: Uint8Array): Promise<SealedBroadcastJson> {
    return sealBroadcastWithKey(snap.keyRef, snap.key, pt);
  }
  async openBroadcast(sealed: SealedBroadcastJson): Promise<Uint8Array> {
    return openBroadcastJson(this.opts.registry, sealed);
  }
  async sealNeedToKnow(dids: string[], subjectTid: string, pt: Uint8Array): Promise<SealedNeedToKnowJson> {
    const recipients = [];
    for (const did of dids) {
      const r = did === this.did
        ? { signingKey: this.#keys.signingKey, encryptionKey: this.#keys.encryptionKey }
        : await this.directory.resolve(did);
      if (!r?.encryptionKey) throw new Error(`client: no encryption key resolved for ${did}`);
      recipients.push({ did, encryptionKey: r.encryptionKey });
    }
    return sealNeedToKnowJson(recipients, subjectTid, pt);
  }
  async openNeedToKnow(sealed: SealedNeedToKnowJson, subjectTid: string): Promise<Uint8Array> {
    return openNeedToKnowJson(sealed, subjectTid, this.#keys);
  }

  /**
   * Rotate BOTH keys (ADR-12): fresh signing + encryption pair, rotateKey
   * {newKey, newEncryptionKey, prev?} signed by the OLD key (chain rule), then
   * local head swap. Returns the fresh recovery bundle — fresh JWKs all round,
   * so the restore path never depends on the previous bundle's encryption JWK.
   * `prev` is omitted (not null — the lexicon rejects null) on first rotation.
   */
  async rotateKeys(cell: string): Promise<{ bundle: IdentityBundle }> {
    if (!this.#announced) throw new Error("client: announce() before rotateKeys()");
    const fresh = await createIdentity(); // donor keypairs; the donor's did is discarded
    const payload: Record<string, unknown> = {
      newKey: fresh.keys.signingKey,
      newEncryptionKey: fresh.keys.encryptionKey,
    };
    if (this.#lastRotationTid !== null) payload.prev = this.#lastRotationTid;
    const env = await buildEnvelope(
      { $type: "app.aid.identity.rotateKey", payload, geo: buildGeo(cell) },
      this.#keys, this.opts.clock, // OLD key signs — chain rule
    );
    // Self-heal a wiped relay (genesis-invalid: the rotateKey's fold has no genesis)
    // before mutating local state — only swap keys/head once the wire actually lands.
    await this.#publishHealing(env.wire, buildGeo(cell));
    this.directory.ingest(env.cbor);
    this.#lastRotationTid = env.wire.id;
    this.#keys = { ...fresh.keys, did: this.#keys.did }; // did:beacon never changes (genesis hash)
    return { bundle: { ...fresh.bundle, did: this.#keys.did } };
  }

  /** Subscriptions are closed individually; the client holds no live resources. */
  async close(): Promise<void> {}
}
